import { z } from "zod";
import { AuditableSchema, DayCountConvention, Side, ValuationTimezone } from "./shared";

enum PricingRule {
  EXCHANGE_DAY_TWAP = "Exchange Day TWAP",
}

enum TerminationRule {
  PERCENT_FIXED_AMOUNT = "Percent of Fixed Amount",
}

enum TransactionType {
  LOAN = "loans",
  EQUITY_SWAP = "equity_swaps",
  SPOT = "spot",
}

enum PaymentSchedule {
  TERMINATION = "Termination",
  END_OF_MONTH = "End of Month",
}

enum SettlementType {
  CASH = "CASH",
  PHYSICAL = "PHYSICAL",
  BOTH = "BOTH",
}

enum EquitySwapEventType {
  EquitySwapBookedEvent = "EquitySwapBookedEvent",
  EquitySwapAmendedEvent = "EquitySwapAmendedEvent",
  EquitySwapPartiallyClosedEvent = "EquitySwapPartiallyClosedEvent",
  EquitySwapClosedEvent = "EquitySwapClosedEvent",
  EquitySwapExpiredEvent = "EquitySwapExpiredEvent",
}

enum EquitySwapEventTypeDisplay {
  EquitySwapBookedEvent = "Swap Booked",
  EquitySwapAmendedEvent = "Swap Amended",
  EquitySwapPartiallyClosedEvent = "Swap Partially Closed",
  EquitySwapClosedEvent = "Swap Closed",
  EquitySwapExpiredEvent = "Swap Expired",
}

const EquitySwapSchema = z.object({
  id: z.string().uuid(),
  uti: z.string().uuid().optional(),
  contract_id: z.string().optional(),
  transaction_margin_groups: z.array(z.any()),
  shares: z.string().min(1),
  number_of_shares: z.string().min(1),
  exchange: z.string().min(1),
  events: z.array(z.any()).optional(),
  initial_price: z.string().min(1),
  initial_price_currency: z.string(),
  trade_date: z.string().optional(),
  effective_date: z.string().optional(),
  termination_date: z.string().min(1),
  fixed_rate: z.string().min(1),
  valuation_time: z.string().optional(),
  valuation_timezone: z.nativeEnum(ValuationTimezone).optional(),
  payment_schedule: z.nativeEnum(PaymentSchedule),
  settlement_type: z.nativeEnum(SettlementType),
  determining_party: z.nativeEnum(Side).optional(),
  settlement_notes: z.string().optional(),
  avging_period: z.string().optional(),
  init_fixed_amount: z.string().optional(),
  day_count: z.nativeEnum(DayCountConvention).optional(),
  early_termination_notes: z.string().optional(),
});

const LoanSchema = z.object({
  id: z.string().uuid(),
  contract_id: z.string(),
  transaction_margin_groups: z.array(z.any()),
  instrument: z.string(),
  principal: z.string().min(1),
  trade_date: z.string().optional(),
  maturity_date: z.string().optional(),
  interest_rate: z.string().min(1),
  guarantor: z.string().optional(),
});

const SpotSchema = z.object({
  id: z.string().uuid(),
  contract_id: z.string(),
  transaction_margin_groups: z.array(z.any()),
  instrument: z.string(),
  price: z.string(),
  size: z.string(),
  trade_date: z.string().optional(),
});

const LoanResourceSchema = LoanSchema.merge(AuditableSchema);

type EquitySwapType = z.infer<typeof EquitySwapSchema>;
type LoanType = z.infer<typeof LoanSchema>;
type LoanResourceType = z.infer<typeof LoanResourceSchema>;
type SpotType = z.infer<typeof SpotSchema>;

export {
  DayCountConvention,
  EquitySwapEventType,
  EquitySwapEventTypeDisplay,
  EquitySwapSchema,
  LoanSchema,
  PaymentSchedule,
  PricingRule,
  SettlementType,
  SpotSchema,
  TerminationRule,
  TransactionType,
};
export type { EquitySwapType, LoanResourceType, LoanType, SpotType };
